import React from 'react';
import PropTypes from 'prop-types';
import { ThemeConsumer } from '@cvent/carina-theme-provider';
import memoize from 'lodash.memoize';
import { toggleSelectedItem, mapTokensToProps } from './util';
import Item from './Item';
import Container from './Container';

/**
 * A group of interactive elements that can be toggled active/inactive.
 * By default, only one is allowed to be active at a given time.
 */
export default class ButtonGroup extends React.Component {
  static Item = Item;

  static propTypes = {
    /** React children */
    children: PropTypes.node.isRequired,
    /** Selected state changed */
    onChange: PropTypes.func.isRequired,
    /** Hidden from screen reader */
    accessible: PropTypes.bool,
    /** Screen reader label text */
    accessibilityLabel: PropTypes.string,
    /** Disable default Button minWidths */
    autoWidth: PropTypes.bool,
    /** Initially selected index */
    defaultSelected: PropTypes.number,
    /** Display position relative to content (RTL-aware) */
    iconAlign: PropTypes.oneOf(['start', 'end']),
    /** Right to left language preference */
    isRtl: PropTypes.bool,
    /** Selected index */
    selected: PropTypes.number,
    /** Available size variants */
    size: PropTypes.oneOf(['s', 'm', 'l']),
    /** For e2e tests (maps to `data-cvent-id`) */
    testID: PropTypes.string
  };

  static defaultProps = {
    autoWidth: false,
    isRtl: false,
    size: 'm',
    selected: null,
    defaultSelected: null
  };

  constructor(props) {
    super(props);
    const { selected, defaultSelected } = props;
    this.state = {
      selected: defaultSelected !== null ? defaultSelected : null,
      controlled: selected !== null
    };
    this.mapTokensToProps = memoize(mapTokensToProps);
  }

  /**
   * Take in a list item index and, as a side-effect, update state to
   * toggle active state on or off
   * @param {number} index List item index
   */
  toggleItem = index => {
    let selected;
    if (this.state.controlled) {
      selected = toggleSelectedItem(index, this.props.selected);
    } else {
      selected = toggleSelectedItem(index, this.state.selected);
      this.setState({ selected });
    }
    this.props.onChange(selected);
  };

  render() {
    return (
      <ThemeConsumer>
        {theme => {
          const {
            borderRadius: borderRadiusTokens,
            size: sizeTokens,
            theme: themeTokens,
            text: textTokens,
            iconOnly: iconOnlyTokens,
            border: borderTokens
          } = mapTokensToProps(theme);
          const themeProps = {
            borderRadiusTokens,
            sizeTokens,
            themeTokens,
            textTokens,
            iconOnlyTokens,
            borderTokens
          };
          return (
            <Container
              testID={this.props.testID}
              accessibilityLabel={this.props.accessibilityLabel}
              accessible={this.props.accessible}
              isRtl={this.props.isRtl}
            >
              {this.renderItems(themeProps)}
            </Container>
          );
        }}
      </ThemeConsumer>
    );
  }

  /**
   * Render the individual buttons
   * @param {item[]} items An array of item configs
   * @returns {ReactElement[]} Renderable React element
   */
  renderItems(theme) {
    const shared = {
      isRtl: this.props.isRtl,
      iconAlign: this.props.iconAlign,
      theme,
      autoWidth: this.props.autoWidth,
      size: correctButtonSize(this.props.size.toLowerCase())
    };
    if (!this.props.children) {
      return null;
    }
    const count = React.Children.count(this.props.children);
    let previousItemDisabled = false;
    const { selected } = this.state.controlled ? this.props : this.state;
    return React.Children.map(this.props.children, (element, index) => {
      const position = getPosition(index, count);
      const afterDisabled = !!previousItemDisabled;
      previousItemDisabled = element.props.disabled;
      return React.cloneElement(element, {
        ...shared,
        selected: selected === index,
        position,
        afterDisabled,
        onPress: e => {
          this.toggleItem(index);
          if (element.props.onPress) {
            element.props.onPress(e);
          }
        },
        ...(this.props.testID && {
          _testID: `${this.props.testID}-item-${index}`
        })
      });
    });
  }
}

function getPosition(index, len) {
  if (index === 0) {
    return 'first';
  }
  if (index === len - 1) {
    return 'last';
  }
  return undefined;
}

/* corrects deprecated api, can be removed in future release */
function correctButtonSize(size) {
  switch (size) {
    case 'medium':
      return 'm';
    case 'small':
      return 's';
    case 'large':
      return 'l';
    default:
      return size;
  }
}
